import { t } from "@lingui/macro";
import { useLingui } from "@lingui/react";
import { ArrowLeft, ArrowRight, Heart } from "@phosphor-icons/react";
import { templatesList } from "@stylingcv/utils";
import { motion } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

import { useDynamicCountryDetection } from "@/client/hooks/use-dynamic-country-detection";
import {
  getGulfCountryInfo,
  useGulfCountryDetection,
} from "@/client/utils/gulf-countries-detector";
import { getDefaultMarketInsights, getMarketInsights } from "@/client/utils/market-insights-data";

// Template categories for better organization
const templateCategories = {
  beginners: ["simple", "straight", "essential", "clean"], // Simple, clean designs
  professionals: ["classic", "confidence", "onyx", "leafish", "executive", "modern"], // Corporate, traditional
  creatives: ["highlight", "bold", "framed", "feminine", "slate"], // Colorful, unique layouts
  arabic: ["split", "grid", "metro", "horizon"], // RTL-optimized designs
  // All templates with proven RTL / Arabic support
  arabicTemplates: [
    "split", "grid", "metro", "horizon",
    "rayhan", "vibrant", "executive", "modern",
    "classic", "confidence", "onyx", "leafish",
    "elegant", "essential", "simple",
  ],
};

// User-friendly category names - using a function to access translations
const getCategoryNames = (isRTL: boolean) => ({
  ...(isRTL
    ? { arabicTemplates: "قوالب عربية" }
    : {}),
  beginners: t`For Beginners`,
  professionals: t`For Professionals`,
  creatives: t`For Creatives`,
  arabic: t`For Arabic Jobs`,
  all: t`All Templates`,
});

const getCategoryTranslation = (category: string) => {
  const categoryTranslations: Record<string, string> = {
    beginners: t`Beginners`,
    professionals: t`Professionals`,
    creatives: t`Creatives`,
    arabic: t`Arabic`,
    arabicTemplates: t`Arabic Templates`,
  };
  return categoryTranslations[category];
};

const getTemplateTranslation = (template: string) => {
  const templateTranslations: Record<string, string> = {
    clean: t`Clean`,
    minimal: t`Minimal`,
    pure: t`Pure`,
    classic: t`Classic`,
    split: t`Split`,
    confidence: t`Confidence`,
    creative: t`Creative`,
    essential: t`Essential`,
    elegance: t`Elegance`,
    executive: t`Executive`,
    feminine: t`Feminine`,
    bold: t`Bold`,
    framed: t`Framed`,
    horizon: t`Horizon`,
    sidebar: t`Sidebar`,
    simple: t`Simple`,
    leafish: t`Leafish`,
    vibrant: t`Vibrant`,
    traditional: t`Traditional`,
    accent: t`Accent`,
    compact: t`Compact`,
    metro: t`Metro`,
    modern: t`Modern`,
    "modern-refined": t`Modern Refined`,
    influx: t`Influx`,
    newcast: t`Newcast`,
    nexus: t`Nexus`,
    grid: t`Grid`,
    onyx: t`Onyx`,
    highlight: t`Highlight`,
    straight: t`Straight`,
    rayhan: t`Rayhan`,
    slate: t`Slate`,
    pinnacle: t`Pinnacle`,
    zenith: t`Zenith`,
    ethereal: t`Ethereal`,
    aura: t`Aura`,
    prism: t`Prism`,
    axiom: t`Axiom`,
    vortex: t`Vortex`,
    editorial: t`Editorial`,
    architect: t`Architect`,
    modernist: t`Modernist`,
    studio: t`Studio`,
    folio: t`Folio`,
    cipher: t`Cipher`,
    apex: t`Apex`,
    corsa: t`Corsa`,
  };
  return templateTranslations[template];
};

const getTemplateCategory = (templateName: string) => {
  for (const [category, templates] of Object.entries(templateCategories)) {
    if (templates.includes(templateName)) {
      return category;
    }
  }
  return "professional";
};

const getCategoryColor = (category: string) => {
  const colors: Record<string, string> = {
    professional:
      "bg-[#8B5A2B]/10 dark:bg-[#8B5A2B]/20 text-[#8B5A2B] dark:text-[#D4A574] border-[#8B5A2B]/30 dark:border-[#8B5A2B]/40",
    creative:
      "bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300 border-purple-200 dark:border-purple-800",
    modern:
      "bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 border-green-200 dark:border-green-800",
    minimal:
      "bg-gray-50 dark:bg-gray-900/20 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-800",
  };
  return colors[category] || colors.professional;
};

// Generate random loved counts for templates (15,000-44,000)
const generateLovedCount = (template: string) => {
  // Use template name as seed for consistent random numbers with better distribution
  let hash = 0;
  for (let i = 0; i < template.length; i++) {
    const char = template.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  // Ensure positive number and good distribution
  const seed = Math.abs(hash);
  return Math.floor((seed % 29_000) + 15_000);
};

// Responsive visible templates function
const getVisibleTemplates = () => {
  if (typeof window === "undefined") return 4;
  if (window.innerWidth < 640) return 1; // Mobile: 1 template
  if (window.innerWidth < 768) return 2; // Small tablet: 2 templates
  if (window.innerWidth < 1024) return 3; // Tablet: 3 templates
  return 4; // Desktop and large desktop: 4 templates
};

type TemplatesSectionProps = {
  variant?: "default" | "bottom";
};

export const TemplatesSection = ({ variant = "default" }: TemplatesSectionProps) => {
  const { i18n } = useLingui();
  const isRTL = i18n.locale.startsWith("ar");

  const sectionTitle =
    variant === "bottom"
      ? t`Choose Your Perfect Design`
      : t`Professional Resume Templates`;

  const [currentIndex, setCurrentIndex] = useState(0);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const carouselRef = useRef<HTMLDivElement>(null);

  // Gulf country detection and market insights
  const { detection, loading: gulfLoading } = useGulfCountryDetection();
  const countryData = useDynamicCountryDetection();
  const isGulfUser = detection?.isGulfCountry || false;
  const gulfCountryInfo = detection?.countryCode
    ? getGulfCountryInfo(detection.countryCode as any)
    : null;
  const marketInsights = detection?.countryCode
    ? getMarketInsights(detection.countryCode)
    : getDefaultMarketInsights();

  // Filter templates based on selected category
  const displayTemplates =
    selectedCategory === "all"
      ? templatesList
      : templatesList.filter((template) =>
          templateCategories[selectedCategory as keyof typeof templateCategories]?.includes(template),
        );

  // Reset carousel when category changes
  useEffect(() => {
    setCurrentIndex(0);
  }, [selectedCategory]);

  const [visibleTemplates, setVisibleTemplates] = useState(getVisibleTemplates);

  const nextSlide = () => {
    setCurrentIndex((prevIndex) =>
      prevIndex + visibleTemplates >= displayTemplates.length ? 0 : prevIndex + visibleTemplates,
    );
  };

  const prevSlide = () => {
    setCurrentIndex((prevIndex) => {
      if (prevIndex === 0) {
        // Go to last full page
        return Math.floor((displayTemplates.length - 1) / visibleTemplates) * visibleTemplates;
      }
      return Math.max(0, prevIndex - visibleTemplates);
    });
  };

  const goToSlide = (pageIndex: number) => {
    setCurrentIndex(pageIndex * visibleTemplates);
  };

  // Touch handlers for mobile swipe
  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(0); // Reset touchEnd
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const minSwipeDistance = 50;

    if (isRTL) {
      // RTL: swipe directions are reversed
      if (distance > minSwipeDistance) {
        prevSlide();
      } else if (distance < -minSwipeDistance) {
        nextSlide();
      }
    } else {
      if (distance > minSwipeDistance) {
        nextSlide();
      } else if (distance < -minSwipeDistance) {
        prevSlide();
      }
    }
  };

  // Initialize responsive template count on mount and preload images
  useEffect(() => {
    const initialTemplates = getVisibleTemplates();
    setVisibleTemplates(initialTemplates);

    // Preload first batch of template images
    const preloadImages = async () => {
      const imagePromises = displayTemplates.slice(0, initialTemplates * 2).map((template) => {
        return new Promise((resolve, reject) => {
          const img = new Image();
          img.addEventListener("load", () => {
            resolve(img);
          });
          img.onerror = () => {
            console.warn(`Failed to load template: ${template}.png`);
            resolve(img); // Resolve anyway to not block loading
          };
          img.src = `/templates/png/${template}.png`;
        });
      });

      try {
        await Promise.all(imagePromises);
        setImagesLoaded(true);
      } catch (error) {
        console.warn("Some template images failed to load:", error);
        setImagesLoaded(true); // Set to true anyway to show carousel
      }
    };

    // Start preloading immediately
    preloadImages();
  }, [displayTemplates]);

  // Force initial visibility for first templates
  useEffect(() => {
    if (carouselRef.current) {
      // Ensure first templates are visible immediately
      const firstTemplates = carouselRef.current.querySelectorAll("img");
      Array.from(firstTemplates).forEach((img, index) => {
        if (index < visibleTemplates) {
          img.style.opacity = "1";
          img.style.visibility = "visible";
        }
      });
    }
  }, [visibleTemplates, displayTemplates]);

  // Handle responsive template count
  useEffect(() => {
    const handleResize = () => {
      const newVisibleTemplates = getVisibleTemplates();
      setVisibleTemplates(newVisibleTemplates);
      // Adjust currentIndex to align with page boundaries
      const currentPage = Math.floor(currentIndex / visibleTemplates);
      const newIndex = currentPage * newVisibleTemplates;
      // Ensure the new index doesn't exceed bounds
      if (newIndex >= displayTemplates.length) {
        setCurrentIndex(0);
      } else {
        setCurrentIndex(newIndex);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [currentIndex]);

  // Auto-play functionality (pause on hover)
  useEffect(() => {
    if (isHovered) {
      return; // Don't set interval if hovered
    }

    const interval = setInterval(nextSlide, 6000);
    return () => {
      clearInterval(interval);
    };
  }, [visibleTemplates, displayTemplates.length, isHovered]);

  const totalDots = Math.ceil(displayTemplates.length / visibleTemplates);
  const currentSlideIndex = Math.floor(currentIndex / visibleTemplates);

  // Calculate transform value — positive in RTL, negative in LTR
  const transformValue = isRTL
    ? currentIndex * (100 / visibleTemplates)
    : -currentIndex * (100 / visibleTemplates);

  return (
    <section id="templates" className="relative overflow-hidden bg-[#fdfcfb] py-4 sm:py-12 lg:py-16 xl:py-20">
      {/* Background decorative elements */}
      <div className="pointer-events-none absolute -left-20 -top-20 size-72 rounded-full bg-primary/10 blur-[80px]" />
      <div className="pointer-events-none absolute -bottom-32 -right-32 size-96 rounded-full bg-accent/10 blur-[100px]" />

      <div className="container relative z-10 px-4 sm:px-6 lg:px-8">
        {/* Header Section */}
        <div className="mx-auto mb-8 max-w-4xl text-center sm:mb-10 lg:mb-14">
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mb-4 text-center text-3xl font-bold leading-tight text-foreground sm:mb-6 sm:text-4xl lg:text-5xl"
          >
            {sectionTitle}
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mb-8 text-center text-lg leading-relaxed text-muted-foreground sm:text-xl"
          >
            {countryData && !countryData.loading
              ? t`Complete collection of ATS-optimized templates with Arabic & English support for ${countryData.countryName} market`
              : t`Proven templates designed by HR experts and top typographers.`}
          </motion.p>

          {/* Category Filter Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="flex flex-nowrap items-center justify-start sm:justify-center gap-2 sm:gap-3 overflow-x-auto pb-4 px-1 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"
          >
            {Object.entries(getCategoryNames(isRTL)).map(([key, name]) => (
              <button
                key={key}
                className={`shrink-0 rounded-full px-4 py-2 text-sm font-semibold transition-all duration-300 sm:px-6 sm:py-3 sm:text-base ${
                  selectedCategory === key
                    ? "scale-105 bg-gradient-to-r from-[#E8885E] to-[#D77045] text-white shadow-[0_4px_14px_0_rgba(232,136,94,0.39)] border-transparent"
                    : "border border-white/20 bg-white/5 text-muted-foreground hover:scale-105 hover:bg-white/10 hover:text-white backdrop-blur-sm"
                }`}
                onClick={() => {
                  setSelectedCategory(key);
                }}
              >
                {name}
              </button>
            ))}
          </motion.div>
        </div>

        {/* Templates Carousel Container - Optimized height for proper display */}
        <div className="relative mx-auto max-w-md sm:max-w-4xl md:max-w-6xl lg:max-w-[90rem] xl:max-w-[100rem] 2xl:max-w-[110rem]">
          {/* Left Arrow */}
          <button
            className="absolute left-1 top-[35%] z-20 -translate-y-1/2 rounded-full border border-border bg-white/10 p-3 text-white shadow-xl backdrop-blur-md transition-all duration-200 hover:scale-110 hover:bg-white/20 sm:left-4 sm:p-4 lg:-left-16 lg:p-5"
            aria-label={t`Previous templates`}
            onClick={prevSlide}
          >
            <ArrowLeft size={16} className="sm:size-5 lg:size-6" />
          </button>

          {/* Right Arrow */}
          <button
            className="absolute right-1 top-[35%] z-20 -translate-y-1/2 rounded-full border border-border bg-white/10 p-3 text-white shadow-xl backdrop-blur-md transition-all duration-200 hover:scale-110 hover:bg-white/20 sm:right-4 sm:p-4 lg:-right-16 lg:p-5"
            aria-label={t`Next templates`}
            onClick={nextSlide}
          >
            <ArrowRight
              size={16}
              className="sm:size-5 lg:size-6"
            />
          </button>

          {/* Templates Carousel */}
          <div
            ref={carouselRef}
            className="min-h-[650px] w-full touch-pan-y overflow-hidden rounded-xl"
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            onMouseEnter={() => {
              setIsHovered(true);
            }}
            onMouseLeave={() => {
              setIsHovered(false);
            }}
          >
            <div
              className="flex transition-transform duration-700 ease-out"
              style={{
                transform: `translateX(${transformValue}%)`,
                willChange: "transform",
              }}
            >
              {displayTemplates.map((template, index) => {
                const lovedCount = generateLovedCount(template);
                const category = getTemplateCategory(template);
                const categoryColor = getCategoryColor(category);

                return (
                  <div
                    key={template}
                    className="shrink-0 px-1 sm:px-2 lg:px-2 xl:px-2"
                    style={{ width: `${100 / visibleTemplates}%` }}
                  >
                    <Link to={`/builder/try?template=${template}&data=1&lang=${isRTL ? "ar" : "en"}`} className="block">
                      <motion.div
                        initial={{
                          opacity: 1,
                        }}
                        animate={{
                          opacity: 1,
                        }}
                        transition={{ duration: 0.3 }}
                        whileHover={{
                          transition: { duration: 0.3, ease: "easeOut" },
                        }}
                        className="group flex min-h-[400px] cursor-pointer flex-col overflow-visible rounded-xl bg-white shadow-lg transition-all duration-300 hover:scale-105 hover:shadow-xl dark:bg-gray-800"
                      >
                        {/* Template Image Container */}
                        <div className="relative flex-1 p-4">
                          <div className="relative m-2 aspect-[210/297] overflow-hidden rounded-lg bg-white shadow-md">
                            <img
                              src={`/templates/png/${template}.png`}
                              alt={`${template} template`}
                              loading={index < visibleTemplates * 2 ? "eager" : "lazy"}
                              className="size-full object-cover transition-all duration-300"
                            />

                            {/* Hover Overlay with Use This Style Button */}
                            <div className="absolute inset-0 flex items-center justify-center bg-black/20 opacity-0 transition-all duration-300 group-hover:opacity-100">
                              <div className="scale-90 rounded-lg bg-emerald-500 px-6 py-3 font-semibold text-white shadow-lg transition-all duration-200 hover:bg-emerald-600 group-hover:scale-100">
                                {t`Use This Style`} →
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Template Info - Compact */}
                        <div className="shrink-0 space-y-2 px-2 pb-2">
                          <div className="flex items-center justify-between">
                            <h3 className="text-sm font-semibold capitalize text-gray-900 dark:text-white">
                              {getTemplateTranslation(template) || (template
                                ? template.charAt(0).toUpperCase() + template.slice(1)
                                : "Template")}
                            </h3>
                            <span
                              className={`rounded-full border px-2 py-1 text-xs font-medium ${categoryColor}`}
                            >
                              {getCategoryTranslation(category) || (category
                                ? category.charAt(0).toUpperCase() + category.slice(1)
                                : category)}
                            </span>
                          </div>

                          {/* Love Count */}
                          <div className="flex items-center justify-center gap-1.5 text-gray-500 dark:text-gray-400">
                            <Heart size={12} className="text-red-500 sm:size-3.5" weight="fill" />
                            <span className="text-xs">
                              {t`${lovedCount.toLocaleString()} users chose this template`}
                            </span>
                          </div>
                        </div>
                      </motion.div>
                    </Link>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Pagination Dots */}
          <div className="mt-10 flex justify-center gap-2">
            {Array.from({ length: totalDots }).map((_, index) => {
              const slideNumber = index + 1;
              return (
                <button
                  key={index}
                  className={`h-2 rounded-full transition-all duration-300 ${
                    currentSlideIndex === index
                      ? "w-8 bg-teal-400"
                      : "w-2 bg-white/20 hover:bg-white/40"
                  }`}
                  aria-label={`Go to slide ${slideNumber}`}
                  onClick={() => {
                    goToSlide(index);
                  }}
                />
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};
